import { onMounted, onUnmounted, ref } from 'vue';

/**
 * Result of a custom loader. Resolves when the script is "ready"
 * (flips `isLoaded` to `true`); rejects to populate `error`.
 */
export type ScriptLoader = (url: string) => Promise<void>;

let loaderOverride: ScriptLoader | null = null;

/**
 * Replace the script-loading mechanism used by `useScriptTag` with a custom
 * implementation. Primarily intended for test environments — jsdom does not
 * dispatch `load` events for dynamically inserted `<script>` tags, so any
 * composable that gates behavior on `isLoaded` (e.g. `usePlaidLink`) hangs
 * forever by default.
 *
 * In a test setup file:
 *
 * ```ts
 * import { setScriptLoader } from '@leaflink/stash-vue/useScriptTag';
 * setScriptLoader(() => Promise.resolve());
 * ```
 *
 * Pass `null` to restore the default DOM-based loader. The override applies
 * globally for the lifetime of the module — call once per test worker.
 */
export function setScriptLoader(loader: ScriptLoader | null): void {
  loaderOverride = loader;
}

type UseScriptOptions = {
  manual: boolean;
};

export default function useScriptTag(url: string, options: UseScriptOptions = { manual: false }) {
  const scriptTag = ref<HTMLScriptElement | null>(null);
  const isLoaded = ref(false);
  const error = ref<Error | null>(null);

  // Per-load token used to ignore override Promises that resolve after the
  // component has unmounted (or after a subsequent load has superseded them).
  // The DOM path doesn't need this — it cancels via removeEventListener.
  let activeLoadId = 0;

  const setIsLoaded = () => (isLoaded.value = true);
  const setError = (e: ErrorEvent) => (error.value = e.error);

  function loadScript() {
    if (loaderOverride) {
      isLoaded.value = false;
      error.value = null;

      const id = ++activeLoadId;
      loaderOverride(url).then(
        () => {
          if (id !== activeLoadId) return;
          isLoaded.value = true;
        },
        (err: Error) => {
          if (id !== activeLoadId) return;
          error.value = err;
        },
      );
      return;
    }

    let el = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);

    if (!el) {
      isLoaded.value = false;
      error.value = null;

      el = document.createElement('script');

      el.type = 'text/javascript';
      el.async = true;
      el.src = url;
      el.addEventListener('load', setIsLoaded);
      el.addEventListener('error', setError);
      document.head.appendChild(el);
    } else {
      isLoaded.value = true;
    }

    scriptTag.value = el;
  }

  function unloadScript() {
    if (!document) {
      return;
    }

    if (loaderOverride) {
      activeLoadId++;
      scriptTag.value = null;
      isLoaded.value = false;
      return;
    }

    const elToBeRemoved = document.querySelector<HTMLScriptElement>(`script[src="${url}"]`);

    if (elToBeRemoved) {
      elToBeRemoved.removeEventListener('load', setIsLoaded);
      elToBeRemoved.removeEventListener('error', setError);

      document.head.removeChild(elToBeRemoved);

      scriptTag.value = null;
      isLoaded.value = false;
    }
  }

  if (!options.manual) {
    onMounted(loadScript);
    onUnmounted(unloadScript);
  }

  return { scriptTag, load: loadScript, unload: unloadScript, isLoaded, error };
}
